<script>
  import {
    select as d3Select,
    geoPath as d3GeoPath,
    geoIdentity as d3GeoIdentity,
  } from 'd3'
  import { group as d3Group } from 'd3-array'
  import { gridding as d3Gridding } from 'd3-gridding'

  const d3 = {
    select: d3Select,
    geoPath: d3GeoPath,
    geoIdentity: d3GeoIdentity,
    group: d3Group,
    gridding: d3Gridding,
  }

  // Properties
  let outerHeight = 0
  export { outerHeight as height }
  let outerWidth = 0
  export { outerWidth as width }
  import { data, layers } from 'modules/stores.js'

  // Validation
  $: heightOK = height > 0
  $: widthOK = width > 0
  $: dataOK = Array.isArray(preparedData) && preparedData.length > 0
  $: layersOK = Array.isArray($layers)
  $: svgOK = svg && svg.length === 1 // and test if it's a svg tag?

  // Dimensions
  const toLowerStep = dim => {
    const step = 50
    // some margin to avoid pixel-perfect problems
    const margin = 4
    return Math.floor((dim - margin) / step) * step
  }
  $: height = toLowerStep(outerHeight)
  $: width = toLowerStep(outerWidth)
  $: showChart = height > 0 && width > 0

  // Data preparation
  const prepareData = data =>
    data.map(l => ({ ...l, segment: [[+l.x1, +l.y1], [+l.x2, +l.y2]] }))

  const computeGrid = (values, mode, height, width) => {
    const margin = 12
    const padding = 5
    return d3
      .gridding()
      .size([width - 2 * margin, height - 2 * margin])
      .offset([margin, margin])
      .padding(padding)
      .margin(0)
      .mode(mode)(values)
  }

  const computeGrids = (layers, data, height, width) => {
    const grids = []
    let h = height,
      w = width
    for (const layer of layers) {
      // For each layer: get the unique values for the attribute (ie. the groups)
      const values = Array.from(
        data.reduce((set, row) => {
          set.add(row[layer.attribute])
          return set
        }, new Set())
      )
      const grid = {
        cells: computeGrid(values, layer.mode, h, w),
        attribute: layer.attribute,
      }
      grids.push(grid)

      h = grid.cells[0].height
      w = grid.cells[0].width
    }
    return grids
  }

  const computeSmallestGridExtent = (grids, height, width) => {
    if (grids.length === 0) {
      return [width, height]
    }
    // Get the smallest grid
    const grid = grids[grids.length - 1]
    // Assume that all grid cells have the same size
    // (see https://github.com/romsson/d3-gridding)
    return [grid.cells[0].width, grid.cells[0].height]
  }

  const computeGeoPathFn = (maxPoint, geoPoints) =>
    d3.geoPath(
      d3.geoIdentity().fitExtent([[0, 0], maxPoint], {
        type: 'MultiPoint',
        coordinates: geoPoints,
      })
    )

  // See https://observablehq.com/@severo/til-d3-group-with-arbitrary-keys
  // Note that if layers = [], it returns data (an Array), meanwhile if layers
  // is not empty, the result is a Map object
  const computeGroupedData = (data, layers) => {
    const keys = layers.map(d => e => e[d.attribute])
    return d3.group(data, ...keys)
  }

  // Chart rendering

  const setSvgSize = (svg, height, width) => {
    if (!svgOK || !heightOK || !widthOK) {
      return
    }
    svg
      .attr('height', height)
      .attr('width', width)
      .attr('viewBox', `0 0 ${width} ${height}`)
  }

  /* drawLayer
   *
   * selection:    d3 selection (a collection of DOM elements, with binded data)
   * grids:        an array of grids. each grid is an array of layout 'cells',
   *               which dimensions have been computed with d3-gridding
   * groupedData:  data grouped (with d3.group) by the layers attribute. If
   *               layers is empty, groupedData is an array of data rows. Else,
   *               it's a Map (of Maps (of Maps...)) of Arrays of data rows.
   *               Map keys are the values of the attribute.
   * geoPathFn:    the d3.geoPath function, to plot the rows (using row.segment)
   */
  const drawLayer = (selection, grids, groupedData, geoPathFn) => {
    // Sanity checks
    if (grids.length > 0 && !(groupedData instanceof Map)) {
      throw 'grids is not empty, groupedData should be a Map.'
    }

    if (grids.length === 0) {
      // No grids. Just draw the segments

      // sanity check
      if (!Array.isArray(groupedData)) {
        throw 'grids is empty, groupedData should be an Array.'
      }

      selection
        .append('path')
        .style('stroke-width', 1)
        .style('stroke', 'black')
        .attr(
          'd',
          geoPathFn({
            type: 'MultiLineString',
            coordinates: groupedData.map(l => l.segment),
          })
        )
    } else if (grids.length === 1) {
      // One grid.

      // generate <g> elements, placed at its location
      const g = selection
        .selectAll('g')
        .data(grids[0].cells)
        .join('g')
        .attr('transform', d => `translate(${d.x}, ${d.y})`)

      // add a label
      g.selectAll('text')
        .data(d => [d])
        .join('text')
        .text(d => `${d.__value}`)

      // draw the segments
      g.selectAll('path')
        .data(d => [groupedData.get(d.__value)])
        .join('path')
        .style('stroke-width', 1)
        .style('stroke', 'black')
        .attr('d', d =>
          geoPathFn({
            type: 'MultiLineString',
            coordinates: d.map(l => l.segment),
          })
        )
    } else if (grids.length === 2) {
      // Two intricated grids

      // external grid: generate <g> elements, placed at its location
      const g1 = selection
        .selectAll('g')
        .data(grids[0].cells)
        .join('g')
        .attr('transform', d => `translate(${d.x}, ${d.y})`)

      // add a label
      g1.selectAll('text')
        .data(d => [d])
        .join('text')
        .text(d => `${d.__value}`)

      // internal grid: generate <g> elements, placed at its location
      const g2 = g1
        .selectAll('g')
        .data(d =>
          grids[1].cells.map(c => ({ ...c, __value: [d.__value, c.__value] }))
        )
        .join('g')
        .attr('transform', d => `translate(${d.x}, ${d.y})`)

      // add a label
      g2.selectAll('text')
        .data(d => [d])
        .join('text')
        .text(d => `${d.__value[1]}`)

      // draw the segments (but only where there are data)
      g2.filter(
        d =>
          groupedData.has(d.__value[0]) &&
          groupedData.get(d.__value[0]).has(d.__value[1])
      )
        .selectAll('path')
        .data(d => [groupedData.get(d.__value[0]).get(d.__value[1])])
        .join('path')
        .style('stroke-width', 1)
        .style('stroke', 'black')
        .attr('d', d =>
          geoPathFn({
            type: 'MultiLineString',
            coordinates: d.map(l => l.segment),
          })
        )
    } else if (grids.length === 3) {
      // Three intricated grids (grab your microscope)

      // external grid: generate <g> elements, placed at its location
      const g1 = selection
        .selectAll('g')
        .data(grids[0].cells)
        .join('g')
        .attr('transform', d => `translate(${d.x}, ${d.y})`)

      // add a label
      g1.selectAll('text')
        .data(d => [d])
        .join('text')
        .text(d => `${d.__value}`)

      // intermediate grid: generate <g> elements, placed at its location
      const g2 = g1
        .selectAll('g')
        .data(d =>
          grids[1].cells.map(c => ({ ...c, __value: [d.__value, c.__value] }))
        )
        .join('g')
        .attr('transform', d => `translate(${d.x}, ${d.y})`)

      // add a label
      g2.selectAll('text')
        .data(d => [d])
        .join('text')
        .text(d => `${d.__value[1]}`)

      // internal grid: generate <g> elements, placed at its location
      const g3 = g2
        .selectAll('g')
        .data(d =>
          grids[2].cells.map(c => ({
            ...c,
            __value: [...d.__value, c.__value],
          }))
        )
        .join('g')
        .attr('transform', d => `translate(${d.x}, ${d.y})`)

      // add a label
      g3.selectAll('text')
        .data(d => [d])
        .join('text')
        .text(d => `${d.__value[2]}`)

      // draw the segments (but only where there are data)
      g3.filter(
        d =>
          groupedData.has(d.__value[0]) &&
          groupedData.get(d.__value[0]).has(d.__value[1]) &&
          groupedData
            .get(d.__value[0])
            .get(d.__value[1])
            .has(d.__value[2])
      )
        .selectAll('path')
        .data(d => [
          groupedData
            .get(d.__value[0])
            .get(d.__value[1])
            .get(d.__value[2]),
        ])
        .join('path')
        .style('stroke-width', 1)
        .style('stroke', 'black')
        .attr('d', d =>
          geoPathFn({
            type: 'MultiLineString',
            coordinates: d.map(l => l.segment),
          })
        )
    }
  }

  // Events
  const redraw = (height, width, data, layers) => {
    if (!heightOK || !widthOK || !dataOK || !layersOK) {
      return
    }

    // Prepare data
    const groupedData = computeGroupedData(data, layers)
    const grids = computeGrids(layers, data, height, width)
    const geoPathFn = computeGeoPathFn(
      computeSmallestGridExtent(grids, height, width),
      data.map(l => l.segment).flat(1)
    )

    // Create chart
    setSvgSize(svg, height, width)
    svg.html(null)

    drawLayer(svg, grids, groupedData, geoPathFn)
  }

  // TODO: allow redraw and transition cancellation ?
  // TODO: use functional programming to avoid possible side-effects? how to do
  //       that in Svelte?
  // maybe we could optimize: detect that the groups will be the same, etc.
  // could be done using more stores, one by UI action

  // redraw all if dimensions, data or layers have changed

  let svgNode = undefined
  $: svg = d3.select(svgNode)
  $: redraw(height, width, preparedData, $layers)
  $: preparedData = prepareData($data)
</script>

<style>

</style>

<div
  class="height-full width-full d-flex flex-items-center flex-justify-center">
  {#if showChart}
    <svg {height} {width} bind:this={svgNode} />
  {/if}
</div>
